/**
 * Pinch + double-tap zoom for product images that coexists correctly
 * with the carousel's native swipe-to-change-photo gesture.
 *
 * Key idea: we never lock touch-action ahead of time (that's what broke
 * swipe before). Instead we only take over a gesture the moment we know
 * it's actually a pinch (2 touches) or a pan-while-already-zoomed (1
 * touch, but only once scale > 1). Every other single-finger touch is
 * left completely alone, so the browser's native horizontal swipe for
 * paging through photos keeps working exactly as normal.
 *
 * Gestures:
 *  - Two-finger pinch: zoom in/out, following the pinch center
 *  - Double-tap: toggle between 1x and ~2.4x zoom
 *  - Single-finger drag while zoomed (scale > 1): pan around the image
 *  - Single-finger swipe while at 1x: untouched — carousel handles it
 */
(function () {
  var MAX_SCALE = 4;
  var DOUBLE_TAP_SCALE = 2.4;
  var DOUBLE_TAP_MAX_DELAY = 320;
  var DOUBLE_TAP_MAX_DIST = 34;

  function dist(t1, t2) {
    var dx = t1.clientX - t2.clientX;
    var dy = t1.clientY - t2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function midpoint(t1, t2) {
    return {
      x: (t1.clientX + t2.clientX) / 2,
      y: (t1.clientY + t2.clientY) / 2
    };
  }

  function clamp(v, min, max) {
    return Math.max(min, Math.min(max, v));
  }

  function setupZoomable(container) {
    if (container.dataset.zbZoomBound === '1') return;
    container.dataset.zbZoomBound = '1';

    var img = container.querySelector('img');
    if (!img) return;

    var state = {
      scale: 1,
      x: 0,
      y: 0,
      startDist: 0,
      startScale: 1,
      startX: 0,
      startY: 0,
      dragging: false,
      lastTapTime: 0,
      lastTapX: 0,
      lastTapY: 0,
      gestureStartScale: 1
    };

    img.style.transformOrigin = '0 0';
    img.style.willChange = 'transform';
    img.style.transition = 'transform .25s cubic-bezier(.22,1,.36,1)';

    function apply(withTransition) {
      img.style.transition = withTransition
        ? 'transform .25s cubic-bezier(.22,1,.36,1)'
        : 'none';
      img.style.transform =
        'translate(' + state.x + 'px, ' + state.y + 'px) scale(' + state.scale + ')';
    }

    function bounds() {
      var rect = container.getBoundingClientRect();
      var maxX = 0;
      var maxY = 0;
      if (state.scale > 1) {
        maxX = (rect.width * (state.scale - 1)) / 2;
        maxY = (rect.height * (state.scale - 1)) / 2;
      }
      return { maxX: maxX, maxY: maxY };
    }

    function clampPosition() {
      var b = bounds();
      state.x = clamp(state.x, -b.maxX, b.maxX);
      state.y = clamp(state.y, -b.maxY, b.maxY);
    }

    function reset(withTransition) {
      state.scale = 1;
      state.x = 0;
      state.y = 0;
      apply(withTransition !== false);
      container.classList.remove('zb-image-zoomed');
      container.style.overflow = '';
    }

    function zoomToward(scale, focalX, focalY, withTransition) {
      var rect = container.getBoundingClientRect();
      var relX = focalX - rect.left;
      var relY = focalY - rect.top;

      var prevScale = state.scale;
      state.scale = clamp(scale, 1, MAX_SCALE);
      var ratio = state.scale / prevScale;
      state.x = relX - (relX - state.x) * ratio;
      state.y = relY - (relY - state.y) * ratio;

      clampPosition();
      apply(withTransition);
      var zoomed = state.scale > 1.02;
      container.classList.toggle('zb-image-zoomed', zoomed);
      container.style.overflow = zoomed ? 'hidden' : '';
    }

    // ---- Touch-based pinch (Android / most browsers) ----
    container.addEventListener(
      'touchstart',
      function (e) {
        if (e.touches.length === 2) {
          e.preventDefault();
          state.startDist = dist(e.touches[0], e.touches[1]);
          state.startScale = state.scale;
          state.dragging = false;
        } else if (e.touches.length === 1) {
          var now = Date.now();
          var t = e.touches[0];

          var isDoubleTap =
            now - state.lastTapTime < DOUBLE_TAP_MAX_DELAY &&
            Math.abs(t.clientX - state.lastTapX) < DOUBLE_TAP_MAX_DIST &&
            Math.abs(t.clientY - state.lastTapY) < DOUBLE_TAP_MAX_DIST;

          if (isDoubleTap) {
            e.preventDefault();
            if (state.scale > 1.02) {
              reset(true);
            } else {
              zoomToward(DOUBLE_TAP_SCALE, t.clientX, t.clientY, true);
            }
            state.lastTapTime = 0;
            return;
          }

          state.lastTapTime = now;
          state.lastTapX = t.clientX;
          state.lastTapY = t.clientY;

          if (state.scale > 1.02) {
            state.dragging = true;
            state.startX = t.clientX - state.x;
            state.startY = t.clientY - state.y;
          }
        }
      },
      { passive: false }
    );

    container.addEventListener(
      'touchmove',
      function (e) {
        if (e.touches.length === 2 && state.startDist > 0) {
          e.preventDefault();
          var newDist = dist(e.touches[0], e.touches[1]);
          var mid = midpoint(e.touches[0], e.touches[1]);
          var targetScale = state.startScale * (newDist / state.startDist);
          zoomToward(targetScale, mid.x, mid.y, false);
        } else if (e.touches.length === 1 && state.dragging) {
          e.preventDefault();
          var t = e.touches[0];
          state.x = t.clientX - state.startX;
          state.y = t.clientY - state.startY;
          clampPosition();
          apply(false);
        }
      },
      { passive: false }
    );

    container.addEventListener('touchend', function (e) {
      if (e.touches.length === 0) {
        state.startDist = 0;
        state.dragging = false;
        if (state.scale <= 1.02) {
          reset(true);
        }
      }
    });

    container.addEventListener('touchcancel', function () {
      state.startDist = 0;
      state.dragging = false;
    });

    // ---- Safari's proprietary gesture events ----
    container.addEventListener('gesturestart', function (e) {
      e.preventDefault();
      state.gestureStartScale = state.scale;
    });
    container.addEventListener('gesturechange', function (e) {
      e.preventDefault();
      var rect = container.getBoundingClientRect();
      var cx = rect.left + rect.width / 2;
      var cy = rect.top + rect.height / 2;
      zoomToward(state.gestureStartScale * e.scale, cx, cy, false);
    });
    container.addEventListener('gestureend', function (e) {
      e.preventDefault();
      if (state.scale <= 1.02) reset(true);
    });
  }

  function scan() {
    document
      .querySelectorAll('.product-media-container--zoomable, .product-media-container--image')
      .forEach(setupZoomable);
  }

  document.addEventListener('DOMContentLoaded', scan);

  var observer = new MutationObserver(function () {
    scan();
  });
  document.addEventListener('DOMContentLoaded', function () {
    observer.observe(document.body, { childList: true, subtree: true });
  });
})();
